import { useState } from "react";
import { PageTemplate } from "@/views/PageTemplate";
import { TokenOption } from "@/components/ui/types";
import { TokenInput } from "@/components/ui/TokenInput";
import usdcTokenImage from "@/assets/tokens/usdc.svg?url";
import ethTokenImage from "@/assets/tokens/eth.svg?url";
import binanceTokenImage from "@/assets/tokens/binance.svg?url";
import bridgeIcon from "@/assets/icons/bridge.svg?url";

export function BridgePage() {
  // Define tokens
  const usdcToken: TokenOption = {
    id: "usdc",
    name: "USDC",
    icon: <img src={usdcTokenImage} alt='USDC' className='w-6 h-6 rounded-full' />,
  };

  // Define networks
  const networks = [
    { id: "ethereum", name: "Ethereum Network", icon: <img src={ethTokenImage} alt='Ethereum' className='w-6 h-6 rounded-full' /> },
    { id: "binance", name: "Binance Network", icon: <img src={binanceTokenImage} alt='Binance' className='w-6 h-6 rounded-full' /> }, // Using ETH icon as a placeholder for Binance
  ];

  // State declarations
  const [amount, setAmount] = useState("642.25");
  const [sourceNetwork, setSourceNetwork] = useState(networks[0]);
  const [destinationNetwork, setDestinationNetwork] = useState(networks[1]);

  const handleBridge = () => {
    console.log("Bridge executed:", { amount, sourceNetwork, destinationNetwork });
  };

  return (
    <PageTemplate title='BRIDGE' subtitle='Move your tokens between blockchain networks.'>
      <div className='max-w-[792px] mx-auto'>
        <div className='rounded-xl border border-gray-200 overflow-hidden'>
          <div className='bg-white p-6 md:p-8'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* Source Network */}
              <div>
                <p className='text-gray-700 mb-2 text-left text-xs'>Bridge from the...</p>
                <div className='border border-gray-200 rounded-lg overflow-hidden'>
                  <div className='flex items-center p-4'>
                    <div className='flex items-center gap-2'>
                      {sourceNetwork.icon}
                      <span className='text-black'>{sourceNetwork.name}</span>
                    </div>
                    <div className='ml-auto'>
                      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M6 9L12 15L18 9' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Destination Network */}
              <div>
                <p className='text-gray-700 mb-2 text-left text-xs'>Bridge to the...</p>
                <div className='border border-gray-200 rounded-lg overflow-hidden'>
                  <div className='flex items-center p-4'>
                    <div className='flex items-center gap-2'>
                      {destinationNetwork.icon}
                      <span className='text-black'>{destinationNetwork.name}</span>
                    </div>
                    <div className='ml-auto'>
                      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M6 9L12 15L18 9' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Token Input Fields */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mt-6'>
              {/* Source Token */}
              <div>
                <div className='border border-gray-200 rounded-lg overflow-hidden'>
                  <div className='flex items-center'>
                    <div className='flex items-center gap-2 pl-4'>
                      {usdcToken.icon}
                      <span className='text-black'>USDC</span>
                      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M6 9L12 15L18 9' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                      </svg>
                    </div>
                    <input
                      type='text'
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder='0'
                      className='flex-1 w-full p-4 text-right text-black text-xl font-regular focus:outline-none'
                    />
                  </div>
                  <div className='text-left border-t border-gray-200 px-4 py-3 bg-gray-50'>
                    <p className='text-gray-600 text-sm'>Current Balance: 642.25 USDC</p>
                  </div>
                </div>
              </div>

              {/* Destination Token */}
              <div>
                <div className='border border-gray-200 rounded-lg overflow-hidden'>
                  <div className='flex items-center'>
                    <div className='flex items-center gap-2 pl-4'>
                      {usdcToken.icon}
                      <span className='text-black'>USDC</span>
                      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M6 9L12 15L18 9' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                      </svg>
                    </div>
                    <input
                      type='text'
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder='0'
                      className='flex-1 w-full p-4 text-right text-black text-xl font-regular focus:outline-none'
                      readOnly
                    />
                  </div>
                  <div className='text-left border-t border-gray-200 px-4 py-3 bg-gray-50'>
                    <p className='text-gray-600 text-sm'>Current Balance: 0.00 USDC</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction Review */}
          <div className='bg-gray-50 p-6 md:p-8'>
            <h3 className='font-medium text-gray-800 mb-4 text-left text-xs'>Transaction Review</h3>
            <div className='space-y-1 text-sm'>
              <div className='flex items-start'>
                <svg className='w-5 h-5 mr-3 text-gray-600 flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
                <span className='text-gray-700 text-left'>You are bridging 642.25 USDC tokens on the Ethereum Network to the Binance Network.</span>
              </div>
              <div className='flex items-start'>
                <svg className='w-5 h-5 mr-3 text-gray-600 flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
                <span className='text-gray-700 text-left'>
                  Based on the current{" "}
                  <a href='#' className='underline text-gray-700'>
                    slippage settings
                  </a>{" "}
                  of 0.1%, the minimum you will receive is 641.60 USDC tokens.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bridge Button */}
        <div className='flex justify-center mt-6'>
          <button
            onClick={handleBridge}
            className='flex items-center justify-center gap-2 bg-black text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors'
          >
            Bridge to Binance Network
            <img src={bridgeIcon} alt='Bridge' className='w-4 h-4' />
          </button>
        </div>
      </div>
    </PageTemplate>
  );
}
